import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ResizeAspectLock, SetTempElementSize, MediaElementContentVisibility, MediaOnlyDisplay } from '../resizeTypes';
import { RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import usePrevious from '../../../utils/react/usePrevious';
import { getIsMediaOnlyDisplayPreferred } from '../../media/mediaElementDisplayUtils';
import { getMediaOnlyDisplay } from '../../../../common/elements/utils/elementPropertyUtils';

const SIZE_CHECK_THRESHOLD = 3;

type Params = {
    tempSize: RectSize | null;
    element: ImMNElement;
    resizeAspectLock: ResizeAspectLock;
    nonMediaHeightPxRef: MutableRefObject<number | null>;
    getNonMediaHeight: () => number;
    minHeight: number;
    gridSize: number;
    setTempElementSize: SetTempElementSize;
};

/**
 * React to any changes in the non-media content like captions or titles - usually due to text wrapping
 */
const useNonMediaContentResize = ({
    tempSize,
    element,
    resizeAspectLock,
    nonMediaHeightPxRef,
    getNonMediaHeight,
    minHeight,
    gridSize,
    setTempElementSize,
}: Params): MediaElementContentVisibility => {
    const { SHOW_ALL, SHOW_MEDIA_ONLY, UNSET } = MediaElementContentVisibility;

    const [tempMediaElementVisibility, setTempMediaElementVisibility] = useState<MediaElementContentVisibility>(UNSET);

    // If the element is already set to show media only, we don't need to track non-media content resizing
    const skipMeasure = getMediaOnlyDisplay(element) === MediaOnlyDisplay.PREFERENCE;

    // Map of recorded non-media heights to the lowest element width for that height
    const nonMediaHeightToWidthMap = useRef(new Map());
    const maxRecordedWidth = useRef(0);

    const prevTempSize = usePrevious(tempSize);

    const updateMediaSize = (nonMediaHeightPx: number) => {
        nonMediaHeightPxRef.current = nonMediaHeightPx;
        setTempElementSize(tempSize);
    };

    const getVisibility = () => {
        if (!tempSize) return UNSET;
        if (resizeAspectLock === ResizeAspectLock.MEDIA) return SHOW_ALL;
        return getIsMediaOnlyDisplayPreferred(element) ? SHOW_MEDIA_ONLY : SHOW_ALL;
    };

    useEffect(() => {
        if (skipMeasure) return;

        setTempMediaElementVisibility(getVisibility());
        if (!tempSize) maxRecordedWidth.current = 0;
    }, [!!tempSize]);

    const updateMap = (latestNonMediaHeightPx: number) => {
        if (!latestNonMediaHeightPx || !tempSize) return;

        if (tempSize.width > maxRecordedWidth.current) maxRecordedWidth.current = tempSize.width;

        const nonMediaHeightGu = latestNonMediaHeightPx / gridSize;
        const lowestWidthForHeight = nonMediaHeightToWidthMap.current.get(nonMediaHeightGu);
        if (lowestWidthForHeight && tempSize.width >= lowestWidthForHeight) return;

        nonMediaHeightToWidthMap.current.set(nonMediaHeightGu, tempSize.width);
    };

    const showFullElementIfValid = () => {
        if (!tempSize) return false;

        // We only need to check if the size has increased
        if (prevTempSize && tempSize.width <= prevTempSize.width && tempSize.height <= prevTempSize.height) return;

        // If we don't have recorded heights for the current size,
        // show the non-media content and allow it to be hidden again if necessary
        const noHeightsRecorded = nonMediaHeightToWidthMap.current.size === 0;
        const widthExceedsRecordedMax = tempSize.width >= maxRecordedWidth.current + SIZE_CHECK_THRESHOLD;
        if (noHeightsRecorded || widthExceedsRecordedMax) {
            setTempMediaElementVisibility(SHOW_ALL);
            return;
        }

        // If no records match, we show the non-media content anyway so we can
        // record the new size and allow it to be hidden again if necessary
        let recordedSize = { height: 0, width: 0 };
        nonMediaHeightToWidthMap.current.forEach((width, height) => {
            if (width <= tempSize.width && width > recordedSize.width) {
                recordedSize = { height, width };
            }
        });

        const maxNonMediaHeight = tempSize.height - minHeight;
        if (recordedSize.height > maxNonMediaHeight) return;

        setTempMediaElementVisibility(SHOW_ALL);
        updateMediaSize(recordedSize.height * gridSize);
    };

    useLayoutEffect(() => {
        if (!tempSize || skipMeasure) return;

        if (tempMediaElementVisibility === SHOW_MEDIA_ONLY) {
            showFullElementIfValid();
            return;
        }

        const latestNonMediaHeightPx = getNonMediaHeight();
        updateMap(latestNonMediaHeightPx);

        // If the new media height is below the minimum then hide the non-media content
        // Don't hide non-media for media aspect lock as the media size doesn't change
        if (resizeAspectLock !== ResizeAspectLock.MEDIA) {
            const newMediaHeight = tempSize.height - latestNonMediaHeightPx / gridSize;
            if (newMediaHeight < minHeight) {
                setTempMediaElementVisibility(SHOW_MEDIA_ONLY);
                updateMediaSize(0);
                return;
            }
        }

        // If the non-media height hasn't changed significantly, do nothing
        // Some browsers can report sub-pixel differences that don't actually impact layout, and can cause cycling between sizes
        if (Math.abs(latestNonMediaHeightPx - (nonMediaHeightPxRef.current || 0)) < 1) return;

        // Update media size for new non-media content height
        updateMediaSize(latestNonMediaHeightPx);
    }, [tempSize, resizeAspectLock, tempMediaElementVisibility]);

    return tempMediaElementVisibility;
};

export default useNonMediaContentResize;
